import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material';
import { fonts } from '@theme/fontsCustomer';
import { PauseIcon } from '@icons/PauseIcon';
import { PlayIcon } from '@icons/PlayIcon';
import { PlugFillSplashIcon } from '@icons/PlugFillSplashIcon';
import { PlugfillIcon } from '@icons/PlugfillIcon';
import { LoadingIcon } from '@icons/loading/LoadingIcon';
import type { SubscriptionConnectionModel } from '@repositories/subscriptionConnectionRepository/models/SubscriptionConnectionModel';

interface Props {
  connection: SubscriptionConnectionModel;
  connectMutatingCount: number;
  isConnectionFetching: boolean;
  onClickConnectBtn: () => void;
  onClickDisconnectBtn: () => void;
  onClickModifyBtn: () => void;
}

export const ConnectionStatusButton = ({
  connection,
  connectMutatingCount,
  isConnectionFetching,
  onClickConnectBtn,
  onClickDisconnectBtn,
  onClickModifyBtn,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [hover, setHover] = useState<boolean>(false);

  let className;
  let handleClick;
  let buttonIcon;
  let buttonLabel;
  switch (connection.status) {
    case 'CONNECTED':
      className = 'connected';
      handleClick = onClickDisconnectBtn;
      buttonIcon = hover ? (
        <PauseIcon width={20} height={20} color={theme.colors['ic-white']} />
      ) : (
        <PlugfillIcon width={20} height={20} color={theme.colors['text-green']} />
      );
      buttonLabel = hover ? t('Subscrib_Connect_Create_11') : t('Subscrib_Connect_Create_05');
      break;
    case 'FAILED':
      className = 'fail';
      handleClick = onClickModifyBtn;
      buttonIcon = hover ? (
        <PlayIcon width={20} height={20} color={theme.colors['ic-white']} />
      ) : (
        <PlugFillSplashIcon width={20} height={20} color={theme.colors['ic-red-dark']} />
      );
      buttonLabel = hover ? t('Subscrib_Connect_Create_24') : t('Subscrib_Connect_Create_06');
      break;
    case 'CONNECTING':
      className = 'connecting';
      handleClick = onClickDisconnectBtn;
      if (connectMutatingCount > 0 || isConnectionFetching) {
        buttonIcon = <LoadingIcon />;
        buttonLabel = t('Connection_Detail_04');
      } else {
        buttonIcon = hover ? <PauseIcon width={20} height={20} color={theme.colors['ic-white']} /> : <LoadingIcon />;
        buttonLabel = hover ? t('Subscrib_Connect_Create_10') : t('Connection_Detail_04');
      }
      break;
    case 'DISCONNECTED':
    default:
      className = 'pause';
      handleClick = onClickConnectBtn;
      buttonIcon = hover ? (
        <PlayIcon width={20} height={20} color={theme.colors['ic-white']} />
      ) : (
        <PauseIcon width={20} height={20} color={theme.colors['ic-gray-dark']} />
      );
      buttonLabel = hover ? t('Subscrib_Connect_Create_09') : t('Subscrib_Connect_Create_11');
      break;
  }

  return (
    <ConnectionType
      className={className}
      onClick={handleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {buttonIcon}
      {buttonLabel}
    </ConnectionType>
  );
};

const ConnectionType = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  width: 100%;
  height: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  ${fonts.Button2};
  &.connected {
    background: ${props => props.theme.colors['bg-green-light']};
    border: 1px solid ${props => props.theme.colors['border-green-light']};
    color: ${props => props.theme.colors['text-green']};
    :hover {
      cursor: pointer;
      color: ${({ theme: { colors } }) => colors['text-white']};
      background: ${({ theme }) => theme.colors['bg-gradient']};
      border: none;
    }
  }
  &.fail {
    background: ${props => props.theme.colors['bg-red-lighter']};
    border: 1px solid ${props => props.theme.colors['border-red-lighter']};
    color: ${props => props.theme.colors['text-red']};
    :hover {
      cursor: pointer;
      color: ${({ theme: { colors } }) => colors['text-white']};
      background: ${({ theme }) => theme.colors['bg-gradient']};
      border: none;
    }
  }
  &.pause {
    background: ${props => props.theme.colors['bg-gray-light-m']};
    border: 1px solid ${props => props.theme.colors['border-gray-light']};
    color: ${props => props.theme.colors['text-gray-sub-dark']};
    :hover {
      cursor: pointer;
      color: ${({ theme: { colors } }) => colors['text-white']};
      background: ${({ theme }) => theme.colors['bg-gradient']};
      border: none;
    }
  }
  &.connecting {
    background: ${props => props.theme.colors['bg-gradient']};
    border: none;
    color: ${props => props.theme.colors['text-white']};
    :hover {
      cursor: pointer;
      color: ${({ theme: { colors } }) => colors['text-red']};
      background: ${({ theme }) => theme.colors['bg-red-lighter']};
      border: 1px solid ${props => props.theme.colors['border-red-lighter']};
    }
  }
`;
